import React, { useEffect, useMemo, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import LogoutIcon from '@mui/icons-material/Logout'
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import MainDashboardSidebar from '../components/MainDashboardSidebar'
import {
  clearLoginSession,
  getBoardInfo,
  getLoginSession,
  getProfileDeviceIds,
  getUserDisplayInfo,
  getUserProfile,
} from '../services/authApi'
import './MainDashboardPage.css'
import './GreenScorePage.css'

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return {}
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function getSiteName(deviceId, boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)
  const device = payload.config?.Device || {}

  return device.Name || device.Site || device.Location || device.ID || deviceId
}

function getGreenScoreRow(deviceId, boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)
  const data = payload.data || {}
  const greenScore = toNumber(data.greenScore ?? payload.ml?.greenScore ?? payload.greenScore)

  return {
    site: getSiteName(deviceId, boardInfo),
    deviceId,
    greenScore,
  }
}

async function loadGreenScoreRow(deviceId) {
  try {
    const boardInfo = await getBoardInfo(deviceId)

    return getGreenScoreRow(deviceId, boardInfo)
  } catch (error) {
    console.warn(`Unable to fetch GreenScore for ${deviceId}:`, error)

    return {
      site: deviceId,
      deviceId,
      greenScore: null,
    }
  }
}

function getScoreTone(score) {
  const number = toNumber(score)

  if (number === null) {
    return 'unrated'
  }

  if (number >= 80) {
    return 'high'
  }

  if (number >= 50) {
    return 'mid'
  }

  return 'low'
}

function getScoreTier(score) {
  const number = toNumber(score)

  if (number === null) {
    return 'Unrated'
  }

  if (number >= 90) {
    return 'Champion'
  }

  if (number >= 80) {
    return 'Eco Pro'
  }

  if (number >= 50) {
    return 'Builder'
  }

  return 'Needs Focus'
}

function formatScore(score) {
  const number = toNumber(score)

  return number === null ? 'N/A' : number.toFixed(1)
}

function getScoreSummary(rows) {
  const scoredRows = rows.filter((row) => toNumber(row.greenScore) !== null)

  if (!scoredRows.length) {
    return {
      average: null,
      top: null,
      lowest: null,
    }
  }

  const sortedRows = [...scoredRows].sort((a, b) => b.greenScore - a.greenScore)
  const total = scoredRows.reduce((sum, row) => sum + row.greenScore, 0)

  return {
    average: total / scoredRows.length,
    top: sortedRows[0],
    lowest: sortedRows[sortedRows.length - 1],
  }
}

function GreenScorePage({ isDarkMode = false, onSelectTheme }) {
  const [scoreRows, setScoreRows] = useState([])
  const [userInfo, setUserInfo] = useState(() => getUserDisplayInfo(getLoginSession()?.user))
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const sortedRows = useMemo(() => {
    return [...scoreRows].sort((a, b) => (toNumber(b.greenScore) ?? -1) - (toNumber(a.greenScore) ?? -1))
  }, [scoreRows])
  const summary = useMemo(() => getScoreSummary(scoreRows), [scoreRows])

  useEffect(() => {
    let isMounted = true
    const loginSession = getLoginSession()
    const sessionUser = loginSession?.user || {}

    if (sessionUser.email || sessionUser.name || sessionUser.fullName || sessionUser.firstName) {
      setUserInfo(getUserDisplayInfo(sessionUser))
    }

    if (!sessionUser.email) {
      return () => {
        isMounted = false
      }
    }

    setIsLoading(true)
    setLoadError('')

    getUserProfile(sessionUser.email)
      .then(async (profileResponse) => {
        const profileUser = profileResponse?.user || {}
        const deviceIds = getProfileDeviceIds(profileResponse)

        if (isMounted) {
          setUserInfo(getUserDisplayInfo({ ...sessionUser, ...profileUser }))
        }

        if (!deviceIds.length) {
          return []
        }

        return Promise.all(deviceIds.map(loadGreenScoreRow))
      })
      .then((rows) => {
        if (isMounted) {
          setScoreRows(rows)
        }
      })
      .catch((error) => {
        console.error('GreenScore scoreboard request failed:', error)

        if (isMounted) {
          setScoreRows([])
          setLoadError('Unable to load GreenScore data.')
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <main className="dashboard-page greenscore-page">
      <section className="dashboard-shell" aria-label="CNERGI GreenScore scoreboard">
        <MainDashboardSidebar />

        <div className="dashboard-main">
          <header className="dashboard-header">
            <div className="dashboard-header__welcome">
              <span>CNERGI</span>
              <h1>Welcome, {userInfo.firstName}!</h1>
              <p>{userInfo.email}</p>
            </div>

            <div className="dashboard-header__actions">
              <div className="dashboard-theme-switch" role="group" aria-label="Theme selection">
                <button
                  className={`dashboard-theme-choice ${!isDarkMode ? 'dashboard-theme-choice--active' : ''}`}
                  type="button"
                  onClick={() => onSelectTheme?.('light')}
                >
                  <span className="dashboard-theme-choice__swatch" aria-hidden="true"></span>
                  Default
                </button>

                <button
                  className={`dashboard-theme-choice ${isDarkMode ? 'dashboard-theme-choice--active' : ''}`}
                  type="button"
                  onClick={() => onSelectTheme?.('dark')}
                >
                  <span className="dashboard-theme-choice__swatch" aria-hidden="true"></span>
                  Midnight
                </button>
              </div>

              <div className="dashboard-user">
                <AccountCircleIcon aria-hidden="true" />
                <strong>{userInfo.displayName}</strong>
              </div>

              <a className="dashboard-logout" href="/login" onClick={clearLoginSession}>
                <LogoutIcon aria-hidden="true" />
                Logout
              </a>
            </div>
          </header>

          <section className="greenscore-panel" aria-labelledby="greenscore-title">
            <div className="greenscore-panel__topline">
              <h2 id="greenscore-title">GreenScore Scoreboard</h2>
              <span>Live ranking</span>
            </div>

            <div className="greenscore-summary" aria-label="GreenScore summary">
              <article className="greenscore-summary__card">
                <WorkspacePremiumIcon aria-hidden="true" />
                <span>Average GreenScore</span>
                <strong className={`greenscore-score greenscore-score--${getScoreTone(summary.average)}`}>
                  {formatScore(summary.average)}
                </strong>
                <p>{getScoreTier(summary.average)}</p>
              </article>

              <article className="greenscore-summary__card">
                <EmojiEventsIcon aria-hidden="true" />
                <span>Top GreenScore</span>
                <strong className={`greenscore-score greenscore-score--${getScoreTone(summary.top?.greenScore)}`}>
                  {formatScore(summary.top?.greenScore)}
                </strong>
                <p>{summary.top?.site || 'No scored site'}</p>
              </article>

              <article className="greenscore-summary__card">
                <MilitaryTechIcon aria-hidden="true" />
                <span>Lowest GreenScore</span>
                <strong className={`greenscore-score greenscore-score--${getScoreTone(summary.lowest?.greenScore)}`}>
                  {formatScore(summary.lowest?.greenScore)}
                </strong>
                <p>{summary.lowest?.site || 'No scored site'}</p>
              </article>
            </div>

            <div className="greenscore-table-shell">
              <table className="greenscore-table">
                <thead>
                  <tr>
                    <th scope="col">Rank</th>
                    <th scope="col">Site</th>
                    <th scope="col">GreenScore</th>
                    <th scope="col">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length > 0 ? (
                    sortedRows.map((row, index) => {
                      const tone = getScoreTone(row.greenScore)

                      return (
                        <tr key={row.deviceId || row.site}>
                          <td>
                            <span className={`greenscore-rank greenscore-rank--${index + 1}`}>
                              {index + 1}
                            </span>
                          </td>
                          <td>{row.site}</td>
                          <td>
                            <strong className={`greenscore-score greenscore-score--${tone}`}>
                              {formatScore(row.greenScore)}
                            </strong>
                          </td>
                          <td>
                            <span className={`greenscore-tier greenscore-tier--${tone}`}>
                              {getScoreTier(row.greenScore)}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td className="greenscore-empty" colSpan="4">
                        {isLoading ? 'Loading GreenScores...' : 'No GreenScore records found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {(isLoading || loadError) && (
              <p className="greenscore-status" role="status">
                {isLoading ? 'Loading GreenScores...' : loadError}
              </p>
            )}
          </section>
        </div>
      </section>
    </main>
  )
}

export default GreenScorePage
